import React, { useState } from "react";
import { useClampOverflow } from "@/hooks/useClampOverflow";
import { toArabicNumerals } from "@/lib/arabicNumerals";
import ReviewImageLightbox from "@/components/ReviewImageLightbox";
import { safeExternalUrl } from "@/lib/externalUrl";

type ReviewItem = {
  _id: string;
  text?: string;
  overallRating: number;
  isExternal: boolean;
  externalSource?: string;
  externalAuthor?: string;
  externalDate?: string;
  user?: { full_name: string };
  // Present when an admin linked an imported review to a registered account: the account's
  // name is then the author, not the scraped one.
  assignedUser?: { at: string };
  createdAt: string;
  images?: string[];
  sourceData?: { postUrl?: string; reviewUrl?: string; [key: string]: unknown };
};

type RecentReviewsGridProps = {
  reviews: ReviewItem[];
  totalCount: number;
  onScrollToAllReviews: () => void;
};

function relativeTimeAr(dateStr: string): string {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";

  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "اليوم";
  if (diffDays === 1) return "منذ يوم";
  if (diffDays < 7)
    return `منذ ${toArabicNumerals(diffDays)} أيام`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return weeks === 1
      ? "منذ أسبوع"
      : `منذ ${toArabicNumerals(weeks)} أسابيع`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return months === 1
      ? "منذ شهر"
      : `منذ ${toArabicNumerals(months)} أشهر`;
  }
  const years = Math.floor(diffDays / 365);
  return years === 1
    ? "منذ سنة"
    : `منذ ${toArabicNumerals(years)} سنوات`;
}

function sourceLabel(review: ReviewItem): string {
  if (!review.isExternal) return "يتفرج";
  const src = review.externalSource ?? "";
  if (src.includes("google")) return "جوجل";
  if (src.includes("facebook")) return "فيسبوك جروب";
  return "يتفرج";
}

function RecentReviewCard({ review }: { review: ReviewItem }) {
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  // Whether the 4-line clamp actually hides content is DOM-measured by the
  // shared hook (which also re-measures after the webfont swap). Widened to
  // HTMLElement so the same ref can hold either the <a> (external reviews) or
  // the <p> (internal) branch of the body text.
  const { ref: textRef, isTruncated } = useClampOverflow<HTMLElement>(
    expanded,
    review.text
  );

  const author = review.assignedUser
    ? review.user?.full_name ?? review.externalAuthor ?? "مجهول"
    : review.externalAuthor ?? review.user?.full_name ?? "مجهول";
  const dateStr = review.externalDate ?? review.createdAt;
  const filled = Math.round(review.overallRating);
  const src = sourceLabel(review);

  // Scraped values — only http/https may ever reach an `href` (T-7r4-01).
  const externalUrl = safeExternalUrl(
    review.sourceData?.postUrl ?? review.sourceData?.reviewUrl
  );

  const bodyTextStyle: React.CSSProperties = {
    fontFamily: "var(--ys-font)",
    fontSize: 13.5,
    color: "var(--ys-ink-2)",
    lineHeight: 1.6,
    margin: 0,
    ...(expanded
      ? {}
      : {
          overflow: "hidden",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical" as const,
          WebkitLineClamp: 4,
        }),
  };

  return (
    <>
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background: "var(--ys-paper)",
          borderRadius: "var(--ys-r-md)",
          boxShadow: hovered ? "var(--ys-shadow-3)" : "var(--ys-shadow-1)",
          border: hovered
            ? "1px solid var(--ys-rule-2)"
            : "1px solid var(--ys-rule)",
          padding: 16,
          transform: hovered ? "translateY(-2px)" : "translateY(0)",
          transition:
            "box-shadow 150ms var(--ys-ease), border-color 150ms var(--ys-ease), transform 150ms var(--ys-ease)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 8,
          }}
        >
          <span
            style={{
              fontFamily: "var(--ys-font)",
              fontWeight: 600,
              fontSize: 14,
              color: "var(--ys-ink)",
            }}
          >
            {author}
          </span>
          <span
            style={{
              fontFamily: "var(--ys-font)",
              fontSize: 11.5,
              color: "var(--ys-ink-3)",
              whiteSpace: "nowrap",
            }}
          >
            {relativeTimeAr(dateStr)}
          </span>
        </div>

        {/* Stars */}
        <div
          style={{ direction: "ltr", color: "var(--ys-star)", fontSize: 13 }}
          aria-label={`تقييم ${review.overallRating} من ٥`}
        >
          {[1, 2, 3, 4, 5].map((i) => (
            <span key={i} style={{ opacity: i <= filled ? 1 : 0.25 }}>
              ★
            </span>
          ))}
        </div>

        {/* Body text */}
        {review.text ? (
          <>
            {externalUrl ? (
              <a
                ref={textRef as React.RefObject<HTMLAnchorElement>}
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  ...bodyTextStyle,
                  display: expanded ? "block" : bodyTextStyle.display,
                  textDecoration: "none",
                }}
                onMouseOver={(e) => (e.currentTarget.style.color = "var(--ys-primary)")}
                onMouseOut={(e) => (e.currentTarget.style.color = "var(--ys-ink-2)")}
              >
                {review.text}
              </a>
            ) : (
              <p ref={textRef as React.RefObject<HTMLParagraphElement>} style={bodyTextStyle}>
                {review.text}
              </p>
            )}
            {/* Rendered OUTSIDE the anchor so expanding never navigates away. */}
            {isTruncated && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                style={{
                  fontFamily: "var(--ys-font)",
                  fontSize: 12,
                  color: "var(--ys-primary)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  alignSelf: "flex-start",
                }}
              >
                {expanded ? "عرض أقل" : "عرض المزيد"}
              </button>
            )}
          </>
        ) : (
          <p
            style={{
              fontFamily: "var(--ys-font)",
              fontSize: 13,
              color: "var(--ys-ink-3)",
              fontStyle: "italic",
              margin: 0,
            }}
          >
            بدون نص
          </p>
        )}

        {/* Images */}
        {review.images && review.images.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {review.images.slice(0, 3).map((img, idx) => (
              <button
                key={idx}
                onClick={() => setLightboxIdx(idx)}
                style={{
                  position: "relative",
                  width: 56,
                  height: 56,
                  borderRadius: "var(--ys-r)",
                  overflow: "hidden",
                  border: "1px solid var(--ys-rule)",
                  cursor: "pointer",
                  padding: 0,
                  flexShrink: 0,
                }}
              >
                <img
                  src={img}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    // Expired/broken source URLs (e.g. old FB CDN links) — hide the tile
                    const btn = e.currentTarget.parentElement;
                    if (btn) btn.style.display = "none";
                  }}
                />
                {idx === 2 && review.images!.length > 3 && (
                  <span
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(0,0,0,.5)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "var(--ys-font)",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    +{toArabicNumerals(review.images!.length - 3)}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Source dot */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: "auto",
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "var(--ys-primary)",
              flexShrink: 0,
              display: "inline-block",
            }}
          />
          <span
            style={{
              fontFamily: "var(--ys-font)",
              fontSize: 11.5,
              color: "var(--ys-ink-3)",
            }}
          >
            {src}
          </span>
        </div>
      </div>

      {/* Rendered as a SIBLING of the card, never a child: the card carries
          transform: translateY(...), which would make it the containing block
          for a position: fixed overlay and clip the lightbox to the card.
          The shared component also portals to document.body. */}
      <ReviewImageLightbox
        images={review.images ?? []}
        index={lightboxIdx}
        onChange={setLightboxIdx}
        onClose={() => setLightboxIdx(null)}
      />
    </>
  );
}

export default function RecentReviewsGrid({
  reviews,
  totalCount,
  onScrollToAllReviews,
}: RecentReviewsGridProps) {
  if (reviews.length === 0) return null;

  const visible = reviews.slice(0, 3);

  return (
    <div>
      <style>{`
        .rrg-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        .rrg-card { min-width: 0; }
        @media (max-width: 640px) {
          .rrg-cards {
            display: flex;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            scroll-behavior: smooth;
            gap: 12px;
            padding: 0 40px 6px;
            margin-inline: -40px;
            scroll-padding-inline: 40px;
            scrollbar-width: none;
          }
          .rrg-cards::-webkit-scrollbar { display: none; }
          .rrg-card {
            flex: 0 0 80vw;
            max-width: 320px;
            scroll-snap-align: start;
          }
        }
      `}</style>

      {/* Heading */}
      <h2
        style={{
          fontFamily: "var(--ys-font)",
          fontWeight: 700,
          fontSize: 20,
          color: "var(--ys-ink)",
          margin: "0 0 16px 0",
        }}
      >
        آخر التقييمات
      </h2>

      {/* cards — grid on desktop, horizontal scroll on mobile */}
      <div className="rrg-cards">
        {visible.map((r) => (
          <div key={r._id} className="rrg-card">
            <RecentReviewCard review={r} />
          </div>
        ))}
      </div>

      {/* Scroll CTA */}
      <div style={{ textAlign: "center", marginTop: 16 }}>
        <button
          onClick={onScrollToAllReviews}
          style={{
            fontFamily: "var(--ys-font)",
            fontSize: 13,
            color: "var(--ys-ink-2)",
            background: "var(--ys-bg-2)",
            border: "1px solid var(--ys-rule-2)",
            borderRadius: "var(--ys-r-pill)",
            padding: "8px 18px",
            cursor: "pointer",
          }}
        >
          اقرأ كل التقييمات ({toArabicNumerals(totalCount)}) ↓
        </button>
      </div>
    </div>
  );
}
